import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { format } from "date-fns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enUS as enUSPicker } from "@mui/x-date-pickers/locales";

import { enUS, fr, de } from "date-fns/locale";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import {
	useCreateCustomEventMutation,
	useResetCustomEventAccessMutation,
	useUpdateCustomEventMutation,
} from "@/pages/Private/redux/customEvent/customEvent.api";

import { CustomEventSourceType, EventType } from "@/enum/custom-event.enum";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";

import { classNames } from "@/utils/classNames";

import { Toggle } from "@/components/Toggle/Toggle";

import { Information } from "@/components/Information/Information";
import { Modal } from "@/components/Modals/Modal";

import { List } from "../../List/schema/list";
import { CreateCustomEvent, CreateCustomEventSchema, CustomEvent } from "../schema/customEvent";
import { RescrapeActions } from "./RescrapeActions";

interface CreateCustomEventFormProps {
	customEvent?: CustomEvent | null;
	list: List;
	/** Shown once a file has been delivered and no re-scrape is already on its way (PREC-3091). */
	canRequestRescrape?: boolean;
	isRescrapePending?: boolean;
	isCancelling?: boolean;
	handleRequestRescrape?: () => void;
	handleCancelRescrape?: () => void;
}

export const CreateCustomEventForm: FunctionComponent<CreateCustomEventFormProps> = ({
	customEvent,
	list,
	canRequestRescrape = false,
	isRescrapePending = false,
	isCancelling = false,
	handleRequestRescrape,
	handleCancelRescrape,
}) => {
	const userLocale = navigator.language || "en-US";

	const localeMap = {
		"en-US": enUS,
		"en-GB": enUS, // you can alias if you don’t mind slight differences
		"fr-FR": fr,
		"de-DE": de,
		de: de,
		en: enUS,
		fr: fr,
	};

	const currentLocale = localeMap?.[userLocale as keyof typeof localeMap] || enUS;

	const { t } = useTranslation();
	const ts = (key: string) => t(`customEvent.${key}`);
	const [createCustomEvent, { error, isLoading }] = useCreateCustomEventMutation();
	const [updateCustomEvent, { error: updateError, isLoading: updateIsLoading }] =
		useUpdateCustomEventMutation();
	const [accessProvided, setAccessProvided] = useState<boolean>(false);
	const [showResetAccessModal, setShowResetAccessModal] = useState<boolean>(false);
	const [resetCustomEventAccess, { isLoading: resetAccessIsLoading }] =
		useResetCustomEventAccessMutation();
	const [loginNeeded, setLoginNeeded] = useState<boolean>(false);

	let in14days = new Date();

	in14days.setDate(new Date().getDate() + 14);

	if (customEvent?.listProvidedUntil) {
		in14days = new Date(customEvent.createdAt);
		const listProvidedUntil = new Date(customEvent?.listProvidedUntil);

		in14days.setDate(in14days.getDate() + 14);

		in14days.setHours(
			listProvidedUntil.getHours(),
			listProvidedUntil.getMinutes(),
			listProvidedUntil.getSeconds(),
			listProvidedUntil.getMilliseconds()
		);
	}

	const InitialState = useMemo(() => {
		return {
			eventType: EventType.ATTENDEES,
			sourceType: CustomEventSourceType.WEBSITE,
		};
	}, []);

	const [currentFormState, setCurrentFormState] = useState<Partial<CreateCustomEvent>>(
		customEvent
			? {
					...customEvent,
					listProvidedUntil: new Date(customEvent.listProvidedUntil),
					sourceType: customEvent.sourceType || CustomEventSourceType.WEBSITE,
				}
			: InitialState
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSaveFormState = (value: any, name: keyof CreateCustomEvent) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const {
		handleSubmit,
		reset,
		setValue,
		formState: { errors, isDirty },
	} = useForm<CreateCustomEvent>({
		defaultValues: InitialState,
		resolver: zodResolver(CreateCustomEventSchema),
	});

	useEffect(() => {
		if (customEvent) {
			reset({
				id: customEvent.id,
				name: customEvent.name,
				url: customEvent.url,
				eventType: customEvent.eventType,
				listProvidedUntil: new Date(customEvent.listProvidedUntil),
				loginNotes: customEvent.loginNotes,
				sourceType: customEvent.sourceType || CustomEventSourceType.WEBSITE,
			});

			if (customEvent.credentialsSaved) {
				setAccessProvided(true);
			}
		} else {
			reset(InitialState);
		}
	}, [InitialState, customEvent, reset]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	/**
	 * Forgetting the shared credentials is a server-side delete (PREC-3091) — the local flag alone
	 * would only hide the row while 1Password kept holding them.
	 */
	const handleResetAccess = async () => {
		if (!customEvent?.id) {
			return;
		}

		try {
			await resetCustomEventAccess({ customEventId: customEvent.id, listId: list.id }).unwrap();
			setAccessProvided(false);
			setLoginNeeded(true);
			setShowResetAccessModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	const onSubmit = async (values: CreateCustomEvent) => {
		try {
			if (customEvent?.id) {
				await updateCustomEvent({ id: customEvent.id, ...values, listId: list.id }).unwrap();
			} else {
				await createCustomEvent({ ...values, listId: list.id }).unwrap();
			}
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<div className="p-6 rounded-xl border border-border-light bg-background-light">
			<div className="text-smd text-text font-bold mb-5">Request Scraping</div>
			<div className="flex flex-row gap-4 w-full mb-4 flex-wrap">
				<div className="w-auto">
					<Button
						color={
							currentFormState.sourceType === CustomEventSourceType.WEBSITE
								? ButtonColor.ACTION
								: ButtonColor.ACTION_SECONDARY
						}
						size={ButtonSize.ML}
						title="Website"
						onClick={() => handleSaveFormState(CustomEventSourceType.WEBSITE, "sourceType")}
					/>
				</div>
				<div className="w-auto">
					<Button
						color={
							currentFormState.sourceType === CustomEventSourceType.MOBILE_APP
								? ButtonColor.ACTION
								: ButtonColor.ACTION_SECONDARY
						}
						size={ButtonSize.ML}
						title="Mobile app"
						onClick={() => handleSaveFormState(CustomEventSourceType.MOBILE_APP, "sourceType")}
					/>
				</div>
			</div>
			<div className="flex flex-row gap-4 w-full mb-4">
				<InputField
					error={!!errors.name?.message}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						handleSaveFormState(event.target.value, "name");
					}}
					label={"Source Name"}
					name={"name"}
					placeholder={"Source Name"}
					value={currentFormState?.name || ""}
				/>
			</div>
			<div className="flex flex-row gap-4 w-full mb-4">
				<InputField
					error={!!errors.url?.message}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						handleSaveFormState(event.target.value, "url");
					}}
					label={"URL of the website / mobile app download link (iOS)"}
					name={"url"}
					placeholder={"e.g. https://play.google.com/store/search?q=example&c=apps"}
					showError={!!errors.url?.message}
					value={currentFormState?.url || ""}
				/>
			</div>

			<div className="flex flex-col w-full mb-4">
				<div className="relative flex items-center justify-between mb-2">
					<label
						className={classNames("block text-sm font-medium text-gray-900 appearance-none")}
						htmlFor={"listProvidedUntil"}
					>
						List should be provided by
						<span className="ml-1">
							<Information information="We need up to 14 days to prepare the list" />
						</span>
					</label>
				</div>
				<div className="relative">
					<LocalizationProvider
						adapterLocale={currentLocale}
						dateAdapter={AdapterDateFns}
						localeText={enUSPicker.components.MuiLocalizationProvider.defaultProps.localeText}
					>
						<DatePicker
							minDate={in14days}
							name="listProvidedUntil"
							slotProps={{
								field: {
									readOnly: true,
								},
							}}
							sx={{
								width: "100%",
								height: 48,
								marginBottom: 0,
								"& .MuiPickersInputBase-root": {
									backgroundColor: "white",
									height: 48,
									marginBottom: 0,
									padding: "10px 12px",
								},
								"& .MuiPickersSectionList-root": {
									padding: 0,
								},
								"& .MuiPickersOutlinedInput-notchedOutline, & .MuiPickersInputBase-root:hover": {
									borderColor: "#E5E7EB !important",
								},
								"& .MuiFilledInput-root": {
									backgroundColor: "white",
									minHeight: 48,
									marginBottom: 0,
									borderRadius: 1.5,
									paddingTop: "10px",
									paddingBottom: "10px !important",
									paddingLeft: 1.5,
									"& .MuiFilledInput-input": {
										padding: 0,
									},
									border: errors.listProvidedUntil?.message ? "1px solid red" : "none",
								},
							}}
							value={currentFormState.listProvidedUntil || null}
							onChange={(value) => {
								if (value !== null) {
									const formattedDate = new Date(format(value, "yyyy-MM-dd"));

									handleSaveFormState(formattedDate, "listProvidedUntil");
								}
							}}
						/>
					</LocalizationProvider>
				</div>
			</div>
			<div className="flex flex-row gap-4 w-full mb-4">
				<InputField
					className="w-full min-h-[100px]"
					error={!!errors.loginNotes?.message}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						handleSaveFormState(event.target.value, "loginNotes");
					}}
					information={
						"This is the briefing for our developers so please be as explicit as possible"
					}
					label={"Detailed instructions of where to find the list"}
					multiline={true}
					name={"loginNotes"}
					placeholder={"e.g. go to page xyz and scrape all companies"}
					value={currentFormState?.loginNotes || ""}
				/>
			</div>

			{!accessProvided && (
				<div className="flex flex-row gap-4 w-full mb-4 items-center">
					<Toggle isChecked={loginNeeded} onChange={() => setLoginNeeded(!loginNeeded)} />
					<div className="text-sm">The list is protected by a login</div>
				</div>
			)}

			{accessProvided ? (
				<div className="flex flex-col gap-2 w-full mb-4">
					<div className="text-sm text-text-meta">
						You already shared your credentials with us
						<span className="ml-1">
							<Information information="To keep your credentials safe we will store them in 1Password" />
						</span>
					</div>
					<Button
						color={ButtonColor.DANGER}
						isLoading={resetAccessIsLoading}
						title={t("customEvent.rescrape.resetAccess")}
						onClick={() => setShowResetAccessModal(true)}
					/>
				</div>
			) : (
				loginNeeded && (
					<>
						<div className="text-smm mb-4">
							Login Details
							<span className="ml-1">
								<Information information="To keep your credentials safe we will store them in 1Password" />
							</span>
						</div>
						<div className="flex flex-row gap-4 w-full mb-4">
							<InputField
								error={!!errors.username?.message}
								handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
									handleSaveFormState(event.target.value, "username");
								}}
								label={ts("username")}
								name={"username"}
								placeholder={ts("usernamePlaceholder")}
								value={currentFormState?.username || ""}
							/>
						</div>

						<div className="flex flex-row gap-4 w-full mb-4">
							<InputField
								error={!!errors.password?.message}
								handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
									handleSaveFormState(event.target.value, "password");
								}}
								label={ts("password")}
								name={"password"}
								placeholder={ts("passwordPlaceholder")}
								type="password"
								value={currentFormState?.password || ""}
							/>
						</div>

						<div className="flex flex-row gap-4 w-full mb-4">
							<InputField
								className="w-full min-h-[100px]"
								error={!!errors.otpSecret?.message}
								handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
									handleSaveFormState(event.target.value, "otpSecret");
								}}
								label={ts("otpSecret")}
								multiline={true}
								name={"otpSecret"}
								placeholder={ts("otpSecretPlaceholder")}
								value={currentFormState?.otpSecret || ""}
							/>
						</div>
					</>
				)
			)}

			<RescrapeActions
				canRequestRescrape={canRequestRescrape}
				handleCancelRescrape={handleCancelRescrape}
				handleRequestRescrape={handleRequestRescrape}
				isCancelling={isCancelling}
				isRescrapePending={isRescrapePending}
			/>

			<div className="mb-2">
				{getAllErrors(error || updateError, formErrors).length
					? renderErrorMessages(getAllErrors(error || updateError, formErrors))
					: null}
			</div>
			{showResetAccessModal && (
				<Modal
					handleClose={() => setShowResetAccessModal(false)}
					handleSave={handleResetAccess}
					isLoading={resetAccessIsLoading}
					isOpened={showResetAccessModal}
					size="sm"
					submitButtonColor="error"
					submitButtonText={t("customEvent.rescrape.resetAccess")}
					title={t("customEvent.rescrape.resetAccessTitle")}
				>
					<div className="text-sm text-text-meta">
						{t("customEvent.rescrape.resetAccessConfirmation")}
					</div>
				</Modal>
			)}
			<div className="flex gap-4 w-full justify-end border-t border-border-light pt-4">
				{customEvent && (
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						title={t("basics.cancel")}
						onClick={() => {
							if (customEvent) {
								reset({
									id: customEvent.id,
									name: customEvent.name,
									url: customEvent.url,
									eventType: customEvent.eventType,
									listProvidedUntil: new Date(customEvent.listProvidedUntil),
									sourceType: customEvent.sourceType || CustomEventSourceType.WEBSITE,
									loginNotes: customEvent.loginNotes,
								});
							} else {
								reset(InitialState);
							}
						}}
					/>
				)}
				<Button
					disabled={!isDirty}
					isLoading={isLoading || updateIsLoading}
					title={customEvent?.id ? t("basics.save") : t("customEvent.requestButton")}
					onClick={handleSubmit(onSubmit)}
				/>
			</div>
		</div>
	);
};
